import { ChangeEvent, useCallback } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';

import { PillLabel, PillInputStyled, PillIcon } from './pill.style';
import { InlineSvgIds } from '../inline-svg/inline-svg';

type BaseProps = {
  fieldName: string;
  hasBorder?: boolean;
  hasError: boolean;
  iconId?: InlineSvgIds;
  isIconPicker?: boolean;
  isMultiSelect?: boolean;
  isSmall?: boolean;
  validation?: RegisterOptions;
  centered?: boolean;
};

type Props = BaseProps & Omit<JSX.IntrinsicElements['input'], 'ref'>;

const PillInput = ({
  checked,
  children,
  isMultiSelect,
  onClick,
  isIconPicker,
  isSmall,
  fieldName,
  hasBorder,
  hasError,
  iconId,
  id,
  validation,
  centered,
  ...props
}: Props) => {
  const { register } = useFormContext();
  const { onChange } = props;
  const { onChange: registerOnChange } = register(fieldName, validation);

  const onChangeHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      registerOnChange(e);
      onChange?.(e);
    },
    [onChange, registerOnChange]
  );

  return (
    <>
      <PillInputStyled
        type={isMultiSelect ? 'checkbox' : 'radio'}
        id={id}
        onClick={onClick}
        data-selector={`pills-item${checked ? '--selected' : ''}`}
        aria-checked={!!checked}
        aria-invalid={hasError}
        aria-describedby={`${fieldName}-error-message`}
        {...register(fieldName, validation)}
        {...props}
        onChange={onChangeHandler}
      />
      <PillLabel
        $isIconPicker={isIconPicker}
        $hasBorder={hasBorder}
        htmlFor={id}
        $centered={centered}
        $hasIcon={!!iconId}
        $isSmall={isSmall}
        data-selector="pills-item-label"
        data-testid={`pills-item-label-${id}`}
      >
        {iconId && <PillIcon id={iconId} />}
        {children}
      </PillLabel>
    </>
  );
};

export default PillInput;
